import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import {
  MdPeople,
  MdAssessment,
  MdBusiness,
  MdViewModule,
  MdAdd,
  MdEdit,
} from "react-icons/md";
import { createApi } from "../../../../core/api";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const phoneRegex = /^\+?[0-9]{7,15}$/;

export const CompanyList = () => {
  const api = useMemo(() => createApi(), []);
  const navigate = useNavigate();
  const { setPageMenus } = useOutletContext<any>();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<any>({
    companyName: "",
    taxNumber: "",
    taxOffice: "",
    address: "",
    country: "",
    city: "",
    phone: "",
    email: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.company.getAll();
      if (r?.ok) setData(r.data?.data ?? r.data ?? []);
      else setData([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    setPageMenus([
      {
        label: "Companies",
        items: [{ label: "Companies", route: "/dashboard/company" }],
      },
    ]);
    load();
    return () => setPageMenus([]);
  }, [load, setPageMenus]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      companyName: "",
      taxNumber: "",
      taxOffice: "",
      address: "",
      country: "",
      city: "",
      phone: "",
      email: "",
    });
    setErrors({});
    setOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setForm({
      companyName: row.companyName ?? "",
      taxNumber: row.taxNumber ?? "",
      taxOffice: row.taxOffice ?? "",
      address: row.address ?? "",
      country: row.country ?? "",
      city: row.city ?? "",
      phone: row.phone ?? "",
      email: row.email ?? "",
    });
    setErrors({});
    setOpen(true);
  };

  const validate = () => {
    const e: Record<string, string> = {};

    if (!form.companyName.trim()) e.companyName = "Company name is required";

    if (!form.taxNumber.trim()) e.taxNumber = "Tax number is required";

    if (!form.address.trim()) e.address = "Address is required";

    if (!form.country.trim()) e.country = "Country is required";

    if (!form.city.trim()) e.city = "City is required";

    if (!form.email.trim()) e.email = "Email is required";
    else if (!emailRegex.test(form.email)) e.email = "Invalid email format";

    if (!form.phone.trim()) e.phone = "Phone is required";
    else if (!phoneRegex.test(form.phone)) e.phone = "Invalid phone number";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (saving) return;
    if (!validate()) return;

    setSaving(true);

    try {
      if (editing) {
        const r = await api.company.update(editing.id, form);
        if (!r?.ok) throw new Error(r?.message || "Update failed");
      } else {
        const r = await api.company.create(form);
        if (!r?.ok) throw new Error(r?.message || "Create failed");
      }

      setOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo<any[]>(
    () => [
      {
        key: "id",
        title: "ID",
        minWidth: 80,
        accessor: (r: any) => r.id,
      },
      {
        key: "company",
        title: "Company",
        minWidth: 320,
        accessor: (r: any) => r.companyName,
        render: (_: any, v: string) => (
          <Badge
            icon={MdBusiness}
            label={v}
            color="#0EA5E9"
            bg="rgba(14,165,233,.15)"
          />
        ),
      },
      {
        key: "actions",
        title: "Actions",
        minWidth: 240,
        render: (row: any) => (
          <GroupButton
            items={[
              {
                id: "edit",
                icon: MdEdit,
                color: "#2563EB",
                onClick: () => openEdit(row),
              },
              {
                id: "users",
                icon: MdPeople,
                color: "#2563EB",
                onClick: () =>
                  navigate(`/dashboard/company-list/users/${row.id}`),
              },
              {
                id: "modules",
                icon: MdViewModule,
                color: "#0EA5E9",
                onClick: () =>
                  navigate(`/dashboard/company-list/modules/${row.id}`),
              },
              {
                id: "charts",
                icon: MdAssessment,
                color: "#16A34A",
                onClick: () =>
                  navigate(`/dashboard/company-list/analytics/${row.id}`),
              },
            ]}
          />
        ),
      },
    ],
    [navigate]
  );

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Companies</div>
          <div style={S.subTitle}>All registered companies</div>
        </div>

        <Button variant="primary" icon={MdAdd} onClick={openCreate}>
          Create Company
        </Button>
      </div>

      <Table
        tableKey="company-list"
        loading={loading}
        columns={columns}
        data={data}
        features={{
          sortable: true,
          filterable: true,
          pagination: true,
          persistence: true,
        }}
      />

      <Modal open={open} onClose={() => (!saving ? setOpen(false) : null)}>
        <div style={S.modalTitle}>
          {editing ? "Update Company" : "Create Company"}
        </div>

        <div style={S.form}>
          {Object.keys(form).map((k) => (
            <div key={k}>
              <input
                value={form[k]}
                placeholder={k}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                style={{
                  ...S.input,
                  borderColor: errors[k] ? "#DC2626" : "#CBD5E1",
                }}
                disabled={saving}
              />
              {errors[k] && <div style={S.error}>{errors[k]}</div>}
            </div>
          ))}
        </div>

        <div style={S.actions}>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={saving}
            disabled={saving}
          >
            Save
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  wrap: { position: "absolute", inset: 0, padding: 20, background: "#F8FAFC" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: { fontSize: 18, fontWeight: 600, color: "#0F172A" },
  subTitle: { fontSize: 13, color: "#64748B" },
  modalTitle: { fontSize: 18, fontWeight: 800, marginBottom: 14 },
  form: { display: "flex", flexDirection: "column", gap: 12 },
  input: {
    height: 42,
    borderRadius: 10,
    border: "1px solid #CBD5E1",
    padding: "0 12px",
    fontSize: 14,
    width: "100%",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
  error: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: 600,
    color: "#DC2626",
  },
};
